import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { title: "", excerpt: "", cover: "", body: "", tags: "", published: true };

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p, tags: (p.tags || []).join(", ") }); setEditId(p.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editId) await api.put(`/admin/blog/${editId}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete article?")) return;
    await api.delete(`/admin/blog/${id}`);
    load();
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex justify-between items-center mb-8">
        <div><h1 className="font-serif text-3xl text-espresso">Journal</h1><p className="text-bark text-sm">{posts.length} articles</p></div>
        <Button onClick={openNew} data-testid="add-blog-button" className="bg-terracotta text-bone hover:bg-espresso"><Plus className="w-4 h-4 mr-1" /> New article</Button>
      </div>

      <div className="border border-sandline rounded-xl overflow-hidden">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center gap-3 px-4 py-3 border-b border-sandline last:border-0 text-sm" data-testid={`blog-row-${p.id}`}>
            <img src={p.cover} alt="" className="w-12 h-12 rounded object-cover bg-sand" />
            <div className="flex-1">
              <p className="text-espresso font-medium">{p.title}</p>
              <p className="text-bark text-xs">{p.published ? "Published" : "Draft"}</p>
            </div>
            <button onClick={() => openEdit(p)} data-testid={`edit-blog-${p.id}`} className="p-1.5 text-bark hover:text-espresso"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(p.id)} data-testid={`delete-blog-${p.id}`} className="p-1.5 text-bark hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {posts.length === 0 && <p className="p-5 text-bark text-sm">No articles yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-bone max-w-2xl w-[calc(100%-1rem)] max-h-[92vh] overflow-y-auto p-5 md:p-6" data-testid="blog-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-espresso">{editId ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><label className="text-sm text-bark mb-1 block">Title</label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-title-input" className="border-sandline" /></div>
            <div><label className="text-sm text-bark mb-1 block">Cover image URL</label><Input value={form.cover} onChange={(e) => setForm({ ...form, cover: e.target.value })} className="border-sandline" /></div>
            <div><label className="text-sm text-bark mb-1 block">Excerpt</label><Textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="border-sandline" rows={2} /></div>
            <div><label className="text-sm text-bark mb-1 block">Body</label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="blog-body-input" className="border-sandline" rows={8} /></div>
            <div><label className="text-sm text-bark mb-1 block">Tags (comma separated)</label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="border-sandline" /></div>
            <label className="flex items-center gap-2 text-sm text-espresso"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /> Published</label>
            <DialogFooter><Button type="submit" data-testid="save-blog-button" className="bg-terracotta text-bone hover:bg-espresso">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
